"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault").default;
var _interopRequireWildcard = require("@babel/runtime/helpers/interopRequireWildcard").default;
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.ComboboxInput = void 0;
var _extends2 = _interopRequireDefault(require("@babel/runtime/helpers/extends"));
var _toConsumableArray2 = _interopRequireDefault(require("@babel/runtime/helpers/toConsumableArray"));
var _objectWithoutProperties2 = _interopRequireDefault(require("@babel/runtime/helpers/objectWithoutProperties"));
var _propTypes = _interopRequireDefault(require("prop-types"));
var _react = _interopRequireWildcard(require("react"));
var _constants = require("../../constants");
var _ComboboxAdditional = require("../ComboboxAdditional/ComboboxAdditional");
var _FormField = require("../FormField/FormField.context");
var _Input = require("../Input/Input");
var _Tag = require("../Tag/Tag");
var _TagCloseButton = require("../TagCloseButton/TagCloseButton");
var _ComboboxInput = require("./ComboboxInput.styles");
var _excluded = ["appearance", "className", "disabled", "error", "expanded", "id", "limitTagsShown", "onClear", "onKeyDown", "onTagClose", "editable", "renderInput", "renderTag", "size", "tags", "value"];
/**
 * @related ['Input', 'Tag']
 * @since 8.28.0
 */
var ComboboxInput = /*#__PURE__*/(0, _react.forwardRef)(function (_ref, forwardedRef) {
  var appearance = _ref.appearance,
    className = _ref.className,
    disabledProp = _ref.disabled,
    errorProp = _ref.error,
    expanded = _ref.expanded,
    idProp = _ref.id,
    limitTagsShown = _ref.limitTagsShown,
    onClear = _ref.onClear,
    onKeyDown = _ref.onKeyDown,
    onTagClose = _ref.onTagClose,
    editable = _ref.editable,
    renderInput = _ref.renderInput,
    renderTag = _ref.renderTag,
    size = _ref.size,
    tagValues = _ref.tags,
    value = _ref.value,
    rest = (0, _objectWithoutProperties2.default)(_ref, _excluded);
  var inputRef = (0, _react.useRef)();
  var context = (0, _react.useContext)(_FormField.FormFieldContext);
  var disabled = typeof disabledProp !== 'undefined' ? disabledProp : context.disabled;
  var error = typeof errorProp !== 'undefined' ? errorProp : context.error;
  var id = typeof idProp !== 'undefined' ? idProp : context.controlId;

  // Handle click on the tag close button
  var tagCloseCallback = (0, _react.useCallback)(function (tag) {
    if (!disabled && typeof onTagClose === 'function') {
      onTagClose(tag.children);
    }
  }, [disabled, onTagClose]);

  // Focus on the actual input when clicking on the wrapper
  var wrapperClickCallback = (0, _react.useCallback)(function () {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, [inputRef]);

  // Preventing mouse down on the wrapper stops a blur which should not happen since clicking
  // on the wrapper is considered part of the input.
  // [CDS-2229]: We need to use stopPropagation when the dropdown is closed, otherwise
  // there was a weird bug where preventDefault was preventing the mouse event
  // to bubble up to the expand callback
  var wrapperMouseDownCallback = (0, _react.useCallback)(function (e) {
    if (expanded) {
      e.preventDefault();
    } else {
      e.stopPropagation();
    }
  }, [expanded]);

  // Since we prevent default when mouse down on the wrapper, we must stop propagation when
  // mouse down on the input so that the event does not get prevented. If it were to get
  // prevented, we wouldn't be able to select text.
  var inputMouseDownCallback = (0, _react.useCallback)(function (e) {
    return e.stopPropagation();
  }, []);

  // Handle keyboard interactions with the text input field
  var inputKeyDownCallback = (0, _react.useCallback)(function (e) {
    // Close the last option on backspace
    if (!disabled && editable && tagValues && tagValues.length && e.keyCode === _constants.KEY_CODES.BACKSPACE && !value.length && typeof onTagClose === 'function') {
      onTagClose(tagValues[tagValues.length - 1]);
    }

    // Make sure we do not clobber keydown set directly on component
    if (typeof onKeyDown === 'function') {
      onKeyDown(e);
    }
  }, [disabled, onKeyDown, onTagClose, editable, tagValues, value]);
  var clearButtonClickCallback = (0, _react.useCallback)(function (e) {
    // Clicking on the clear button should not focus the input. This prevents the
    // event from bubbling to the wrapper and triggering that focus.
    e.stopPropagation();
    if (!disabled && typeof onClear === 'function') {
      onClear();
    }
  }, [disabled, onClear]);

  // This prevents the clear button from receiving focus.
  var clearButtonMouseDownCallback = (0, _react.useCallback)(function (e) {
    return e.preventDefault();
  }, []);

  // This prevents the click event from bubbling to the wrapper and triggering focus.
  var tagClickCallback = (0, _react.useCallback)(function (e) {
    return e.stopPropagation();
  }, []);

  // This prevents the tags from receiving focus.
  var tagMouseDownCallback = (0, _react.useCallback)(function (e) {
    return e.preventDefault();
  }, []);

  // Display read only tags
  var inputValue = value;
  if (!editable && tagValues && tagValues.length) {
    inputValue = (0, _toConsumableArray2.default)(tagValues);
    if (value) {
      inputValue.push(value);
    }
    inputValue = inputValue.join(', ');
  }
  var renderCloseButton = (0, _react.useCallback)(function (props) {
    return /*#__PURE__*/_react.default.createElement(_TagCloseButton.TagCloseButton, (0, _extends2.default)({}, props, {
      tabIndex: "-1"
    }));
  }, []);
  var tags;
  var additionalTags;
  var clear;
  if (editable && tagValues && tagValues.length) {
    tags = tagValues.map(function (tag) {
      var tagProps = {
        key: tag,
        isOpen: true,
        onClose: tagCloseCallback,
        renderCloseButton: renderCloseButton,
        onMouseDown: tagMouseDownCallback,
        onClick: tagClickCallback,
        children: tag
      };
      return typeof renderTag === 'function' ? renderTag(tagProps) : /*#__PURE__*/_react.default.createElement(_Tag.Tag, tagProps);
    });
    if (typeof limitTagsShown === 'number' && tags.length > limitTagsShown) {
      additionalTags = /*#__PURE__*/_react.default.createElement(_ComboboxAdditional.ComboboxAdditional, null, "+" + (tags.length - limitTagsShown));
      tags = tags.slice(0, limitTagsShown);
    }
    clear = /*#__PURE__*/_react.default.createElement(_TagCloseButton.TagCloseButton, {
      tabIndex: "-1",
      "aria-label": "Clear field",
      onClick: clearButtonClickCallback,
      onMouseDown: clearButtonMouseDownCallback
    });
  }
  var inputProps = Object.assign({}, rest, {
    'aria-expanded': typeof expanded === 'boolean' ? expanded : undefined,
    disabled: disabled,
    error: error,
    id: id,
    'ref': inputRef,
    'onKeyDown': inputKeyDownCallback,
    'onMouseDown': inputMouseDownCallback,
    'readOnly': editable ? undefined : true,
    'value': inputValue
  });
  return /*#__PURE__*/_react.default.createElement(_ComboboxInput.StyledComboboxInput, {
    appearance: appearance,
    className: className,
    isClearable: !!clear,
    disabled: disabled,
    error: error,
    expanded: expanded,
    onClick: wrapperClickCallback,
    onMouseDown: wrapperMouseDownCallback,
    ref: forwardedRef,
    size: size
  }, tags, additionalTags, renderInput(inputProps), clear);
});
exports.ComboboxInput = ComboboxInput;
ComboboxInput.displayName = 'ComboboxInput';
ComboboxInput.propTypes = process.env.NODE_ENV !== "production" ? {
  /**
   * By default, comboboxes have the appearance of a Select
   * component (with an arrow indicator). You can change the appearance to `"input"` to
   * match the Input component.
   */
  appearance: _propTypes.default.oneOf(['input', 'select']),
  /**
   * @ignore
   */
  className: _propTypes.default.string,
  /**
   * Displays the input in a disabled state. Can also be inherited from a FormField parent.
   */
  disabled: _propTypes.default.bool,
  /**
   * Displays the input in an error state. Can also be inherited from a FormField parent.
   */
  error: _propTypes.default.bool,
  /**
   * When the `appearance` is `"select"`, the chevron can be reversed to indicate an expanded
   * state.
   */
  expanded: _propTypes.default.bool,
  /**
   * The native `id` attribute.
   */
  id: _propTypes.default.string,
  /**
   * You can limit the number of tags that are visible.
   */
  limitTagsShown: _propTypes.default.number,
  /**
   * The input's `onChange` callback.
   */
  onChange: _propTypes.default.func,
  /**
   * Called when the clear button is clicked.
   */
  onClear: _propTypes.default.func,
  /**
   * @ignore
   */
  onKeyDown: _propTypes.default.func,
  /**
   * Called when a tag close button is clicked, or when a tag is removed with backspace.
   */
  onTagClose: _propTypes.default.func,
  /**
   * When `false`, tags will render as non-interactive list of values.
   */
  editable: _propTypes.default.bool,
  /**
   * A render function that returns the input component.
   */
  renderInput: _propTypes.default.func,
  /**
   * A render function that returns the tag component.
   */
  renderTag: _propTypes.default.func,
  /**
   * Determines the size of the input.
   */
  size: _propTypes.default.oneOf(Object.values(_constants.INPUT_SIZES)),
  /**
   * An array of tag values to display.
   */
  tags: _propTypes.default.arrayOf(_propTypes.default.string),
  /**
   * The input value.
   */
  value: _propTypes.default.string.isRequired
} : {};
ComboboxInput.defaultProps = {
  appearance: 'select',
  editable: true,
  renderInput: function renderInput(props) {
    return /*#__PURE__*/_react.default.createElement(_Input.Input, props);
  },
  renderTag: function renderTag(props) {
    return /*#__PURE__*/_react.default.createElement(_Tag.Tag, props);
  }
};